import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../services/api';
import { 
  TrendingUp, Users, FileText, MessageSquare, Target, Award, 
  Download, Calendar, Filter, BarChart3, PieChart, Activity 
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

interface OverviewData {
  leads: {
    total: number;
    byStage: Record<string, number>;
    bySource: Record<string, number>;
    byPriority: Record<string, number>;
  };
  applications: {
    total: number;
    byStatus: Record<string, number>;
    byGrade: Record<string, number>;
  };
  communications: {
    total: number;
    byChannel: Record<string, number>;
    byStatus: Record<string, number>;
  };
  conversion: {
    leadsToApplications: string;
    applicationsToAdmissions: string;
  };
}

const Analytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'leads' | 'applications' | 'communications' | 'performance' | 'funnel'>('overview');
  const [loading, setLoading] = useState(false);
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [leadsReport, setLeadsReport] = useState<any>(null);
  const [appsReport, setAppsReport] = useState<any>(null);
  const [commsReport, setCommsReport] = useState<any>(null);
  const [performanceReport, setPerformanceReport] = useState<any>(null);
  const [funnelData, setFunnelData] = useState<any>(null);
  
  const [filters, setFilters] = useState({
    branchId: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branchId) params.append('branchId', filters.branchId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const queryString = params.toString();

      switch (activeTab) {
        case 'overview':
          const overviewRes = await axios.get(`${API_URL}/reports/overview?${queryString}`, {
            headers: { Authorization: `Bearer ${await getAuthToken()}` }
          });
          setOverviewData(overviewRes.data.data);
          break;
        
        case 'leads':
          const leadsRes = await axios.get(`${API_URL}/reports/leads?${queryString}`, {
            headers: { Authorization: `Bearer ${await getAuthToken()}` }
          });
          setLeadsReport(leadsRes.data.data);
          break;
        
        case 'applications':
          const appsRes = await axios.get(`${API_URL}/reports/applications?${queryString}`, {
            headers: { Authorization: `Bearer ${await getAuthToken()}` }
          });
          setAppsReport(appsRes.data.data);
          break;
        
        case 'communications':
          const commsRes = await axios.get(`${API_URL}/reports/communications?${queryString}`, {
            headers: { Authorization: `Bearer ${await getAuthToken()}` }
          });
          setCommsReport(commsRes.data.data);
          break;
        
        case 'performance':
          const perfRes = await axios.get(`${API_URL}/reports/performance?${queryString}`, {
            headers: { Authorization: `Bearer ${await getAuthToken()}` }
          });
          setPerformanceReport(perfRes.data.data);
          break;
        
        case 'funnel':
          const funnelRes = await axios.get(`${API_URL}/reports/funnel?${queryString}`, {
            headers: { Authorization: `Bearer ${await getAuthToken()}` }
          });
          setFunnelData(funnelRes.data.data);
          break;
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    alert('Export functionality coming soon!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics & Reports</h1>
          <p className="text-gray-600">Data-driven insights and performance metrics</p>
        </div>
        <button onClick={handleExport} className="btn-primary">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
            <select
              value={filters.branchId}
              onChange={(e) => setFilters({ ...filters, branchId: e.target.value })}
              className="input"
            >
              <option value="">All Branches</option>
              <option value="kolkata">Kolkata</option>
              <option value="dhanbad">Dhanbad</option>
              <option value="mumbai">Mumbai</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview' as const, label: 'Overview', icon: BarChart3 },
            { id: 'leads' as const, label: 'Leads', icon: Users },
            { id: 'applications' as const, label: 'Applications', icon: FileText },
            { id: 'communications' as const, label: 'Communications', icon: MessageSquare },
            { id: 'performance' as const, label: 'Performance', icon: Award },
            { id: 'funnel' as const, label: 'Funnel', icon: Target }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && overviewData && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Leads</p>
                      <p className="text-2xl font-bold text-gray-900">{overviewData.leads.total}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Applications</p>
                      <p className="text-2xl font-bold text-gray-900">{overviewData.applications.total}</p>
                    </div>
                    <FileText className="w-8 h-8 text-green-400" />
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Communications</p>
                      <p className="text-2xl font-bold text-gray-900">{overviewData.communications.total}</p>
                    </div>
                    <MessageSquare className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Conversion Rate</p>
                      <p className="text-2xl font-bold text-gray-900">{overviewData.conversion.leadsToApplications}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-orange-400" />
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Leads by Stage */}
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Leads by Stage</h3>
                  <div className="space-y-3">
                    {Object.entries(overviewData.leads.byStage).map(([stage, count]) => (
                      <div key={stage} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{stage.replace('_', ' ')}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${(count / overviewData.leads.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Leads by Source */}
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Leads by Source</h3>
                  <div className="space-y-3">
                    {Object.entries(overviewData.leads.bySource).map(([source, count]) => (
                      <div key={source} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{source.replace('_', ' ')}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${(count / overviewData.leads.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Applications by Status */}
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Applications by Status</h3>
                  <div className="space-y-3">
                    {Object.entries(overviewData.applications.byStatus).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{status}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full"
                              style={{ width: `${(count / overviewData.applications.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Communications by Channel */}
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Communications by Channel</h3>
                  <div className="space-y-3">
                    {Object.entries(overviewData.communications.byChannel).map(([channel, count]) => (
                      <div key={channel} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{channel}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-orange-600 h-2 rounded-full"
                              style={{ width: `${(count / overviewData.communications.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Leads Report Tab */}
          {activeTab === 'leads' && leadsReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card">
                  <p className="text-sm text-gray-600">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-900">{leadsReport.total}</p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Avg Lead Score</p>
                  <p className="text-3xl font-bold text-gray-900">{leadsReport.avgLeadScore}</p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Top Priority</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {(Object.entries(leadsReport.byPriority).sort((a: any, b: any) => b[1] - a[1])[0]?.[1] as number) || 0}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">By Stage</h3>
                  <div className="space-y-2">
                    {Object.entries(leadsReport.byStage).map(([stage, count]) => (
                      <div key={stage} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{stage.replace('_', ' ')}</span>
                        <span className="font-semibold">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">By Priority</h3>
                  <div className="space-y-2">
                    {Object.entries(leadsReport.byPriority).map(([priority, count]) => (
                      <div key={priority} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{priority}</span>
                        <span className="font-semibold">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Applications Report Tab */}
          {activeTab === 'applications' && appsReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card">
                  <p className="text-sm text-gray-600">Total Applications</p>
                  <p className="text-3xl font-bold text-gray-900">{appsReport.total}</p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Avg Processing Time</p>
                  <p className="text-3xl font-bold text-gray-900">{appsReport.avgProcessingTime} days</p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Pending Review</p>
                  <p className="text-3xl font-bold text-gray-900">{appsReport.byStatus.pending || 0}</p>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Applications by Status</h3>
                <div className="space-y-3">
                  {Object.entries(appsReport.byStatus).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{status}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-48 bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full"
                            style={{ width: `${(count as number / appsReport.total) * 100}%` }}
                          />
                        </div>
                        <span className="font-semibold w-12">{count as number}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Communications Report Tab */}
          {activeTab === 'communications' && commsReport && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card">
                  <p className="text-sm text-gray-600">Total Communications</p>
                  <p className="text-3xl font-bold text-gray-900">{commsReport.total}</p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Delivery Rate</p>
                  <p className="text-3xl font-bold text-gray-900">{commsReport.deliveryRate}%</p>
                </div>
                <div className="card">
                  <p className="text-sm text-gray-600">Most Used Channel</p>
                  <p className="text-3xl font-bold text-gray-900 capitalize">
                    {Object.entries(commsReport.byChannel).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">By Channel</h3>
                  <div className="space-y-2">
                    {Object.entries(commsReport.byChannel).map(([channel, count]) => (
                      <div key={channel} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{channel}</span>
                        <span className="font-semibold">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">By Recipient Type</h3>
                  <div className="space-y-2">
                    {Object.entries(commsReport.byRecipientType).map(([type, count]) => (
                      <div key={type} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{type}</span>
                        <span className="font-semibold">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Report Tab */}
          {activeTab === 'performance' && performanceReport && (
            <div className="space-y-6">
              <div className="card">
                <h3 className="text-lg font-semibold mb-4">Top Performers</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Leads</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Converted</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {performanceReport.topPerformers.map((performer: any, index: number) => (
                        <tr key={performer.userId}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {performer.userId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {performer.totalLeads}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {performer.converted}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            {performer.conversionRate}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {performer.avgLeadScore.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Funnel Tab */}
          {activeTab === 'funnel' && funnelData && (
            <div className="space-y-6">
              <div className="card">
                <h3 className="text-lg font-semibold mb-6">Conversion Funnel</h3>
                <div className="space-y-4">
                  {Object.entries(funnelData).map(([stage, data]: [string, any], index) => (
                    <div key={stage} className="relative">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize">{stage.replace('_', ' ')}</span>
                        <span className="text-sm text-gray-600">{data.percentage}%</span>
                      </div>
                      <div className="relative">
                        <div className="w-full bg-gray-200 rounded-lg h-12 flex items-center justify-center">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-12 rounded-lg flex items-center justify-center transition-all duration-500"
                            style={{ width: `${data.percentage}%` }}
                          >
                            <span className="text-white font-semibold">{data.count}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;
